import { useState, useContext, useEffect } from "react";

import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import {
  Box,
  Avatar,
  Grid,
  Button,
  Skeleton,
  IconButton,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  Tooltip,
  useTheme
} from "@mui/material";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faChartLineDown,
  faMagnifyingGlassChart,
  faCaretCircleLeft,
  faCaretCircleRight,
  faCircleXmark
} from "@fortawesome/pro-solid-svg-icons";

import {
  ToggleButtons,
  CategoriesAvatar,
  LoadingButton
} from "@aclymatepackages/atoms";
import {
  formatDate,
  ucFirstLetters,
  formatDecimal
} from "@aclymatepackages/formatters";
import { nameToFirstName } from "@aclymatepackages/converters";
import { sumTonsCo2e } from "@aclymatepackages/other-helpers";
import { EmissionsPieChart } from "@aclymatepackages/modules";
import findSubcategory, {
  findScope,
  buildScopesWithColors,
  buildSubcategoriesArray
} from "@aclymatepackages/subcategories";
import {
  filterByDateRange,
  findQuarterDate
} from "@aclymatepackages/date-helpers";
import { buildEmissionGroupData } from "@aclymatepackages/chart-helpers";

import PrimaryViewTable from "./PrimaryViewTable";

import Link from "../../../../atoms/mui/Link";
import Card from "../../../../atoms/mui/Card";
import FeatureErrorBoundary from "../../../../atoms/FeatureErrorBoundary";
import FootprintSummaryDisplay from "../../../../atoms/FootprintSummaryDisplay";
import CsvEmailDownloadButton from "../../../../atoms/buttons/CsvEmailDownloadButton";

import DashboardViewLayout from "../../../../layouts/DashboardViewLayout";
import ViewGraphCardLayout from "../../../../layouts/ViewGraphCard";
import ActionItemsModule from "../../../../modules/ActionItemsModule";
import useToggleableEmissionsDetailsChart from "../../../../modules/charts/useToggleableEmissionsDetailsChart";
import LabeledEmissionsChart from "../../../../modules/charts/LabeledEmissionsChart";
import FilterChips from "../../../../modules/filter/FilterChips";
import DateIntervalToggles from "../../../../modules/DateIntervalToggles";
import Report from "../../../../modules/reporting/Report";

import { AdminDataContext } from "../../../../../helpers/contexts/adminData";
import usePlatformLayoutContext from "../../../../../helpers/contexts/platformLayout";
import useEmissionsContext from "../../../../../helpers/contexts/emissions";
import { useAccountData, useAuth } from "../../../../../helpers/firebase";
import {
  useEmissionsChartData,
  resolveChartBoundaryStartDate
} from "../../../../../helpers/components/emissions";
import { extractScopeThreeEmissions } from "../../../../../helpers/components/scopeThreeEmissions";
import {
  fetchOurApi,
  fetchPeriodTransactions,
  fetchInternalApi
} from "../../../../../helpers/utils/apiCalls";
import useDisplayMeasurementSystem from "../../../../../helpers/hooks/displayMeasurementSystem";
import useSimulationContext from "../../../../../helpers/contexts/simulation";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const findSelectedTimePeriodFilterDates = (selectedTimePeriod, graphPeriod) => {
  if (graphPeriod === "month") {
    const [month, year] = selectedTimePeriod.split("-");
    const startDate = new Date(`20${year}`, month - 1, 1);
    const endDate = new Date(`20${year}`, month, 0);
    return { startDate, endDate };
  }

  if (graphPeriod === "quarter") {
    const [quarter, year] = selectedTimePeriod.split(" ");
    const quarterNumber = quarter.charAt(1);
    const startDate = new Date(year, quarterNumber * 3 - 3, 1);
    const endDate = new Date(year, quarterNumber * 3, 0);
    return { startDate, endDate };
  }

  return {
    startDate: new Date(selectedTimePeriod, 0, 1),
    endDate: new Date(selectedTimePeriod, 11, 31)
  };
};

// prepareFirebaseObj nulls an unparseable date-keyed string rather than
// writing it through, so a simulation transaction's date can be null. new
// Date(null) is a *valid* 1970-01-01 rather than Invalid Date, so it must be
// excluded explicitly -- relying on 1970 happening to fall outside whatever
// range is passed in is not a real guarantee.
export const filterSimulationEmissionsByDateRange = (
  simulationTransactions,
  start,
  end
) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  return simulationTransactions.filter((transaction) => {
    if (!transaction.date) {
      return false;
    }

    const transactionDate = new Date(transaction.date);
    return transactionDate >= startDate && transactionDate <= endDate;
  });
};

const filterEmissionsByChips = ({ emission, selectedChips, viewMode }) => {
  const filterBySubcategory = ({ subcategory }) => {
    if (selectedChips.includes("all")) {
      return true;
    }

    return selectedChips.includes(subcategory);
  };

  const filterByScope = ({ scope }) => {
    if (selectedChips.includes("all")) {
      return true;
    }
    return selectedChips.includes(scope);
  };
  if (viewMode === "subcategories") {
    return filterBySubcategory(emission);
  }
  return filterByScope(emission);
};

const FullTransactionsTable = ({
  selectedTimePeriod,
  graphPeriod,
  viewMode,
  emissions,
  selectedChips
}) => {
  const [user] = useAuth();
  const { viewMode: platformViewMode, isSimulation } =
    usePlatformLayoutContext();
  const { selectedCompany } = useContext(AdminDataContext);

  const [tableEmissions, setTableEmissions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const { transactions: simulationTransactions } = useSimulationContext() || {};

  const { startDate, endDate } = findSelectedTimePeriodFilterDates(
    selectedTimePeriod,
    graphPeriod
  );

  useEffect(() => {
    setTableEmissions([]);
  }, [selectedTimePeriod]);

  const onFetchTransactions = async () => {
    setTransactionsLoading(true);
    const transactions =
      platformViewMode === "admin"
        ? await fetchInternalApi({
            endpoint: "adminFetchPeriodTransactions",
            user,
            body: { companyId: selectedCompany.id, startDate, endDate },
            callback: ({ transactions = [] }) =>
              extractScopeThreeEmissions(transactions)
          })
        : isSimulation
          ? filterSimulationEmissionsByDateRange(
              simulationTransactions,
              startDate,
              endDate
            )
          : await fetchPeriodTransactions({ startDate, endDate });

    const defaultRecurringEmissions = emissions.filter(
      ({ source }) => source !== "aggregated" && source !== "accounting"
    );

    setTableEmissions([...transactions, ...defaultRecurringEmissions]);
    return setTransactionsLoading(false);
  };

  const filteredEmissions = tableEmissions.filter((emission) => {
    if (emission.archived) {
      return false;
    }

    return filterEmissionsByChips({ emission, selectedChips, viewMode });
  });

  return (
    <>
      {tableEmissions.length ? (
        <Grid item>
          <PrimaryViewTable
            viewMode={viewMode}
            emissions={filteredEmissions}
            setEmissions={setTableEmissions}
          />
        </Grid>
      ) : (
        <Grid item container justifyContent="center">
          <Grid item>
            <LoadingButton
              label="Fetch All Emissions"
              variant="contained"
              color="primary"
              isLoading={transactionsLoading}
              onClick={onFetchTransactions}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

const PrimaryViewChart = ({
  graphPeriod,
  setGraphPeriod,
  chartData,
  chartArray,
  emissionsLoading,
  selectedBar,
  setSelectedBar,
  setChartProps
}) => {
  const onBarClick = (barData, idx) => setSelectedBar({ ...barData, idx });

  const onSetGraphPeriod = (period) => {
    setSelectedBar(null);
    return setGraphPeriod(period);
  };

  return (
    <>
      {emissionsLoading ? (
        <Skeleton variant="rectangular" height={250} />
      ) : (
        <ViewGraphCardLayout
          gridProps={{ xs: 12 }}
          title="Emissions History"
          subtitle="Click on any bar to view a summary of emissions during that period."
          graphs={[
            {
              type: "bar",
              icon: faChartBar,
              Graph: LabeledEmissionsChart,
              graphProps: {
                type: "bar",
                onBarClick,
                chartArray,
                setChartProps,
                selectedBar,
                showTooltip: true,
                alternateTooltip: true
              }
            }
          ]}
          action={
            <DateIntervalToggles
              graphPeriod={graphPeriod}
              setGraphPeriod={onSetGraphPeriod}
            />
          }
          color="secondary"
          dataArray={chartData}
          icon={<FontAwesomeIcon icon={faChartLineDown} />}
          analyticsCta="to see how your emissions have changed monthly, quarterly, or yearly"
        />
      )}
    </>
  );
};

// Named export purely for testing this table's date-boundary handling in
// isolation -- PrimaryView itself pulls in enough of its own dependencies
// that mounting the whole dashboard just to cover this wouldn't be worth it.
export const EmissionsSummaryTable = ({
  selectedTimePeriod,
  emissions,
  viewMode,
  graphPeriod
}) => {
  const [{ startDate }] = useAccountData();
  const { convertCarbonUnits, carbonUnitLabel } = useDisplayMeasurementSystem();

  const buildTableData = () => {
    const dataField = viewMode === "scopes" ? "scope" : "subcategory";

    if (graphPeriod === "month") {
      const tableRows =
        viewMode === "scopes"
          ? buildScopesWithColors()
          : buildSubcategoriesArray(emissions);

      const tableData = tableRows.map((row) => {
        const rowEmissions = emissions.filter(
          (emission) => emission[dataField] === row[dataField]
        );

        return {
          ...row,
          emissions: [{ tonsCo2e: sumTonsCo2e(rowEmissions) }]
        };
      });

      const sortedTableData = tableData.sort(
        (a, b) => b.emissions[0].tonsCo2e - a.emissions[0].tonsCo2e
      );

      return {
        tableData: sortedTableData,
        labelsArray: [
          `${selectedTimePeriod} ${ucFirstLetters(carbonUnitLabel)} CO2e`
        ]
      };
    }

    const {
      groupedEmissions,
      scopesArray,
      subcategoriesArray,
      chartLabelsArray
    } = buildEmissionGroupData(
      emissions,
      "month",
      resolveChartBoundaryStartDate(startDate)
    );

    const tableRows = viewMode === "scopes" ? scopesArray : subcategoriesArray;

    const categoriesRowsData = tableRows.map((row) => {
      const groupedRowEmissions = groupedEmissions.map((emissions) =>
        emissions.filter((emission) => emission[dataField] === row[dataField])
      );

      const groupedRowEmissionsSums = groupedRowEmissions.map((emissions) => ({
        tonsCo2e: sumTonsCo2e(emissions)
      }));
      const rowEmissionsTotalTons = groupedRowEmissionsSums.reduce(
        (sum, { tonsCo2e }) => sum + tonsCo2e,
        0
      );

      return {
        ...row,
        tonsCo2e: rowEmissionsTotalTons,
        emissions: [
          ...groupedRowEmissionsSums,
          { tonsCo2e: rowEmissionsTotalTons }
        ]
      };
    });

    const totalRowEmissions = [
      ...groupedEmissions.map((emissions) => ({
        tonsCo2e: sumTonsCo2e(emissions)
      })),
      { tonsCo2e: sumTonsCo2e(emissions) }
    ];

    const tableData = [
      ...categoriesRowsData.sort((a, b) => b.tonsCo2e - a.tonsCo2e),
      { label: "Total", emissions: totalRowEmissions }
    ];

    return {
      tableData,
      labelsArray: [...chartLabelsArray, "Total"]
    };
  };

  const { tableData, labelsArray } = buildTableData();

  return (
    <TableContainer sx={{ maxHeight: "100%", maxWidth: "85vw" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {[
              graphPeriod !== "year" ? "Emission Type" : "",
              ...labelsArray
            ].map((value, idx) => (
              <TableCell key={`header-cell-${idx}`}>
                <Typography
                  noWrap
                  variant={graphPeriod === "year" ? "h6" : "h5"}
                >
                  {value}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map(({ subcategory, scope, emissions, label }, idx) => (
            <TableRow key={`emissions-table-row-${idx}`}>
              <TableCell>
                <Grid container spacing={2} alignItems="center" wrap="nowrap">
                  <Grid item>
                    {!label && (
                      <CategoriesAvatar
                        viewMode={viewMode}
                        subcategory={subcategory}
                        scope={scope}
                      />
                    )}
                  </Grid>
                  {graphPeriod !== "year" && (
                    <Grid item>
                      <Typography variant="body1">
                        {label ||
                          (viewMode === "subcategories"
                            ? ucFirstLetters(findSubcategory(subcategory).name)
                            : `Scope ${scope}`)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </TableCell>
              {emissions.map(({ tonsCo2e }, idx) => (
                <TableCell key={`cell-${idx}`}>
                  <Typography variant="body2">
                    {formatDecimal(convertCarbonUnits(tonsCo2e))}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DetailsEmissionChart = ({ emissions, graphPeriod, viewMode }) => {
  const { chart, toggles } = useToggleableEmissionsDetailsChart(
    emissions,
    graphPeriod === "month" ? "pie" : "bar",
    { height: "100%", aspect: null, viewMode, graphPeriod: "month" },
    viewMode
  );

  if (graphPeriod === "month") {
    return <Box sx={{ height: "100%" }}>{chart}</Box>;
  }

  if (graphPeriod === "quarter") {
    return (
      <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
        <Box flexGrow={1}>{chart}</Box>
        <Box flexGrow={0}>
          <Grid container justifyContent="center">
            <Grid item>{toggles}</Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={8}>
        {chart}
      </Grid>
      <Grid item xs={4}>
        <EmissionsPieChart dataArray={emissions} viewMode={viewMode} />
      </Grid>
    </Grid>
  );
};

const MonthQuarterDetailsBlock = ({
  emissions,
  selectedTimePeriod,
  viewMode,
  graphPeriod,
  selectedChips
}) => {
  return (
    <>
      <Grid item>
        {
          <Grid
            container
            spacing={4}
            sx={{ height: "50vh" }}
            alignItems="stretch"
          >
            <Grid item xs={6} sx={{ height: "100%" }}>
              <EmissionsSummaryTable
                selectedTimePeriod={selectedTimePeriod}
                emissions={emissions}
                viewMode={viewMode}
                graphPeriod={graphPeriod}
              />
            </Grid>
            <Grid item xs={6} sx={{ height: "100%" }}>
              <DetailsEmissionChart
                emissions={emissions}
                graphPeriod={graphPeriod}
                viewMode={viewMode}
              />
            </Grid>
          </Grid>
        }
      </Grid>
      {graphPeriod === "month" && (
        <FullTransactionsTable
          selectedTimePeriod={selectedTimePeriod}
          graphPeriod={graphPeriod}
          viewMode={viewMode}
          emissions={emissions}
          selectedChips={selectedChips}
        />
      )}
    </>
  );
};

const YearDetailsBlock = ({
  emissions,
  selectedTimePeriod,
  viewMode,
  graphPeriod
}) => {
  const [selectedTab, setSelectedTab] = useState("table");

  const tabs = [
    {
      label: "Table",
      value: "table",
      content: (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <EmissionsSummaryTable
            emissions={emissions}
            selectedTimePeriod={selectedTimePeriod}
            viewMode={viewMode}
            graphPeriod={graphPeriod}
          />
        </Box>
      )
    },
    {
      label: "Charts",
      value: "charts",
      content: (
        <Box sx={{ height: "50vh" }}>
          <DetailsEmissionChart
            emissions={emissions}
            viewMode={viewMode}
            graphPeriod={graphPeriod}
          />
        </Box>
      )
    }
  ];

  const { content } = tabs.find(({ value }) => value === selectedTab);

  return (
    <Grid item>
      <Tabs
        value={selectedTab}
        onChange={(_, value) => setSelectedTab(value)}
        fullWidth
      >
        {tabs.map(({ label, value }, idx) => (
          <Tab
            label={label}
            value={value}
            key={`object-details-view-tab-${idx}`}
          />
        ))}
      </Tabs>
      {content}
    </Grid>
  );
};

const IntervalDetailsBlock = ({
  emissions,
  viewMode,
  selectedTimePeriod,
  graphPeriod
}) => {
  const { startDate, endDate } = findSelectedTimePeriodFilterDates(
    selectedTimePeriod,
    graphPeriod
  );

  const { palette } = useTheme();
  const [user] = useAuth();
  const { displayName, email } = user || {};

  const [showReport, setShowReport] = useState(false);
  const [selectedChips, setSelectedChips] = useState(["all"]);

  const buildChipsArray = () => {
    if (viewMode === "subcategories") {
      const uniqueEmissionsSubcategories = [
        ...new Set(emissions.map(({ subcategory }) => subcategory))
      ];

      return uniqueEmissionsSubcategories.map((subcategory) => {
        const subcategoryData = findSubcategory(subcategory);
        return {
          ...subcategoryData,
          id: subcategory
        };
      });
    }

    const uniqueEmissionsScopes = [
      ...new Set(emissions.map(({ scope }) => scope))
    ].filter((scope) => scope);

    return uniqueEmissionsScopes.map((scope) => {
      const { color, name } = findScope(scope);

      return {
        name: `Scope ${name}`,
        id: scope,
        color: palette[color].main
      };
    });
  };

  const filteredEmissions = emissions.filter((emission) =>
    filterEmissionsByChips({ emission, selectedChips, viewMode })
  );

  const onDownloadBtnClick = async () => {
    const buyerId = window.sessionStorage.getItem("accountId");

    return await fetchOurApi({
      path: "/transactions/send-account-transactions-download-email",
      method: "POST",
      data: {
        startDate,
        endDate,
        buyerId,
        emailAddress: email,
        firstName: nameToFirstName(displayName),
        period: selectedTimePeriod
      },
      callback: (res) => res
    });
  };

  const buildReportDate = () => {
    if (graphPeriod === "quarter") {
      const startDate = findQuarterDate(selectedTimePeriod, true);
      const endDate = findQuarterDate(selectedTimePeriod, false);
      return { startDate, endDate };
    }

    const startDate = new Date(selectedTimePeriod, 0, 1);
    const endDate = new Date(selectedTimePeriod, 11, 31);
    return { startDate, endDate };
  };

  const { endDate: reportEndDate } = buildReportDate();
  const isPeriodComplete = dayjs(reportEndDate).isBefore(dayjs(), "day");
  const incompletePeriodTooltip = `This report will be available once the ${graphPeriod} is over.`;

  const sharedDisplayBlockProps = {
    emissions: filteredEmissions,
    viewMode,
    graphPeriod,
    selectedTimePeriod
  };

  return (
    <>
      {showReport && (
        <Report
          {...buildReportDate()}
          setShowReport={setShowReport}
          emissions={emissions}
          graphPeriod={graphPeriod}
        />
      )}
      <Grid container spacing={6} direction="column">
        <Grid
          item
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <FilterChips
              chipsArray={buildChipsArray()}
              selectedChips={selectedChips}
              setSelectedChips={setSelectedChips}
            />
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              {graphPeriod !== "month" && (
                <Grid item>
                  <Tooltip
                    title={isPeriodComplete ? "" : incompletePeriodTooltip}
                  >
                    <span>
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => setShowReport(true)}
                        disabled={!isPeriodComplete}
                      >
                        Show Report
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}
              <Grid item>
                <CsvEmailDownloadButton
                  onDownloadBtnClick={onDownloadBtnClick}
                  tooltip={`Download all emissions for this ${graphPeriod}`}
                  reset={selectedTimePeriod}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {graphPeriod === "year" ? (
          <YearDetailsBlock {...sharedDisplayBlockProps} />
        ) : (
          <MonthQuarterDetailsBlock
            {...sharedDisplayBlockProps}
            selectedChips={selectedChips}
          />
        )}
      </Grid>
    </>
  );
};

const TimePeriodDetails = ({
  chartData,
  emissions,
  graphPeriod,
  viewMode,
  selectedBar,
  setSelectedBar
}) => {
  const { carbonUnitLabel, convertCarbonUnits } = useDisplayMeasurementSystem();

  const barsCount = chartData.length;
  const { idx: selectedBarIdx, label: selectedTimePeriod } = selectedBar;

  const onTimePeriodToggle = (newIdx) =>
    setSelectedBar({ ...selectedBarIdx, ...chartData[newIdx], idx: newIdx });

  const { startDate, endDate } = findSelectedTimePeriodFilterDates(
    selectedTimePeriod,
    graphPeriod
  );

  const periodFilteredEmissions = emissions.filter((emission) =>
    filterByDateRange({
      filterEndDate: endDate,
      filterStartDate: startDate,
      ...emission
    })
  );

  const findTotalEmissionsCount = () => {
    const aggregatedTransactions = periodFilteredEmissions.filter(
      ({ transactionsCount }) => transactionsCount
    );
    const nonAggregatedEmissions = periodFilteredEmissions.filter(
      ({ transactionsCount }) => !transactionsCount
    );
    const aggregatedTransactionsTotalCount = aggregatedTransactions.reduce(
      (sum, { transactionsCount }) => sum + transactionsCount,
      0
    );

    return formatDecimal(
      nonAggregatedEmissions.length + aggregatedTransactionsTotalCount,
      0
    );
  };
  const totalEmissionsSumTons = sumTonsCo2e(periodFilteredEmissions);

  const actionButtons = [
    {
      icon: faCaretCircleLeft,
      onClick: () => onTimePeriodToggle(selectedBarIdx - 1),
      disabled: selectedBarIdx === 0
    },
    {
      icon: faCaretCircleRight,
      onClick: () => onTimePeriodToggle(selectedBarIdx + 1),
      disabled: selectedBarIdx + 1 === barsCount
    },
    {
      icon: faCircleXmark,
      onClick: () => setSelectedBar(null)
    }
  ];

  return (
    <Card
      cardType="layout"
      icon={<FontAwesomeIcon icon={faMagnifyingGlassChart} />}
      title={`${selectedTimePeriod} Emissions Summary`}
      subtitle={`You had ${findTotalEmissionsCount()} emissions this ${graphPeriod} that totaled ${formatDecimal(
        convertCarbonUnits(totalEmissionsSumTons)
      )} ${carbonUnitLabel}`}
      action={
        <Grid container>
          {actionButtons.map(({ icon, onClick, disabled }, idx) => (
            <Grid item key={`action-button-${idx}`}>
              <IconButton onClick={onClick} disabled={disabled}>
                {<FontAwesomeIcon icon={icon} />}
              </IconButton>
            </Grid>
          ))}
        </Grid>
      }
      content={
        <IntervalDetailsBlock
          emissions={periodFilteredEmissions}
          viewMode={viewMode}
          selectedTimePeriod={selectedTimePeriod}
          graphPeriod={graphPeriod}
          barsCount={barsCount}
        />
      }
    />
  );
};

const CarbonBalanceBlock = () => {
  const { currentCarbonBalanceTons, emissionsLoading } = useEmissionsContext();
  const { rootPath } = usePlatformLayoutContext();
  const { convertCarbonUnits, carbonUnitLabel } = useDisplayMeasurementSystem();

  const isPositive = currentCarbonBalanceTons >= 0;
  const color = isPositive ? "secondary" : "error";
  const href = isPositive ? `${rootPath}/badges` : `${rootPath}/purchase`;
  const value = emissionsLoading
    ? "--"
    : formatDecimal(convertCarbonUnits(currentCarbonBalanceTons));

  return (
    <FootprintSummaryDisplay
      color={color}
      label="CARBON BALANCE"
      value={value}
      unit={carbonUnitLabel}
      action={
        <Grid container direction="column" spacing={2} alignItems="flex-end">
          <Grid item>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <Typography variant="body2" color="textSecondary">
                  Carbon Offset Status:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color={color}>
                  {emissionsLoading
                    ? "—"
                    : isPositive
                      ? "Fully Offset"
                      : "Not Offset"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Link href={href}>
              <Button variant="outlined" color={color}>
                {isPositive ? "View Your Badges" : "Buy Carbon Offsets"}
              </Button>
            </Link>
          </Grid>
        </Grid>
      }
    />
  );
};

const PrimaryViewBlock = ({ allEmissions, viewLoading }) => {
  const { palette } = useTheme();
  const [{ startDate: companyStartDate }] = useAccountData();
  const { carbonUnitLabel, convertCarbonUnits } = useDisplayMeasurementSystem();

  const [viewMode, setViewMode] = useState("subcategories");
  const [graphPeriod, setGraphPeriod] = useState("quarter");
  const [selectedBar, setSelectedBar] = useState(null);

  const { chartData, chartArray } = useEmissionsChartData({
    emissions: allEmissions,
    viewMode,
    graphPeriod
  });

  const buildSubtitle = () => {
    const emissionsSumTons = sumTonsCo2e(allEmissions);
    if (!emissionsSumTons) {
      return "There are no emissions from your company that match this filtering criteria.";
    }

    return `Your company has emitted ${formatDecimal(
      convertCarbonUnits(emissionsSumTons)
    )} ${carbonUnitLabel} of CO2 since your start date (${formatDate(
      companyStartDate
    )}).`;
  };

  return (
    <>
      <DashboardViewLayout
        type="primary"
        viewLoading={viewLoading}
        title="Carbon Accounting Summary"
        subtitle={buildSubtitle()}
        showFilterButton={false}
        preGraph={<CarbonBalanceBlock />}
        primaryAction={
          <ToggleButtons
            value={viewMode}
            onChange={(viewMode) => setViewMode(viewMode)}
            buttons={[
              {
                value: "subcategories",
                name: "Types",
                id: "subcategories-option"
              },
              { value: "scopes", name: "Scopes", id: "scopes-option" }
            ]}
          />
        }
        avatar={
          <Avatar style={{ backgroundColor: palette.secondary.main }}>
            <PlaylistAddCheckIcon />
          </Avatar>
        }
        graph={
          <PrimaryViewChart
            chartData={chartData}
            chartArray={chartArray}
            emissionsLoading={viewLoading}
            viewMode={viewMode}
            graphPeriod={graphPeriod}
            setGraphPeriod={setGraphPeriod}
            selectedBar={selectedBar}
            setSelectedBar={setSelectedBar}
          />
        }
        table={
          selectedBar && (
            <Grid item>
              <TimePeriodDetails
                chartData={chartData}
                emissions={allEmissions}
                graphPeriod={graphPeriod}
                viewMode={viewMode}
                selectedBar={selectedBar}
                setSelectedBar={setSelectedBar}
              />
            </Grid>
          )
        }
        footer={<ActionItemsModule />}
      />
    </>
  );
};

const PrimaryView = (props) => {
  return (
    <FeatureErrorBoundary featureName="Dashboard">
      <PrimaryViewBlock {...props} />
    </FeatureErrorBoundary>
  );
};
export default PrimaryView;
